import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { ErrorCode, FileOpener } from '@capawesome-team/capacitor-file-opener';
import { type ErrorLogService as ErrorLogServiceClass } from 'ErrorLogging';
import { angularInjectorProvider } from 'Injector';
import $ from 'jquery';
import { type FontConfig } from '../ShareableGraphics.types';

export function findAndCloneSVG(containerElem: Element, width: number, height: number) {
    const svg = $(containerElem).find('svg')[0];
    const clone = svg.cloneNode(true) as Element;

    // We need to set the width and height on the SVG so that it actually
    // shows up when it gets drawn to the canvas. Otherwise, the canvas
    // appears blank in certain browsers like FF.
    clone.setAttribute('width', width.toString());
    clone.setAttribute('height', height.toString());
    return clone;
}

export async function loadFontAssets(fonts: FontConfig[], svg: Element) {
    if (!fonts?.length) return;

    const fontRequests = fonts.map(fontConfig =>
        fetch(new Request(window.ENDPOINT_ROOT + fontConfig.assetPath))
            .then(resp => resp.arrayBuffer())
            .catch(() => null),
    );

    const fontResponseData = await Promise.all(fontRequests);

    const fontsAsBase64: { [fontFamily: string]: string } = fonts.reduce(
        (obj, fontConfig, i) => ({ ...obj, [fontConfig.fontFamily]: arrayBufferToBase64(fontResponseData[i]!) }),
        {},
    );

    // Add link[rel="preload"] elements for the base64 fonts to the document
    // to ensure they are loaded
    fonts.forEach(fontConfig => {
        const linkElem = getLinkElemForBase64FontFace(fontConfig, fontsAsBase64[fontConfig.fontFamily]);
        document.head.appendChild(linkElem);
    });
    await document.fonts.ready;

    // Embed the base64-url @font-face declarations in the SVG.
    const styleElem = document.createElement('style');
    fonts.forEach(fontConfig => {
        const fontFaceTextNode = getTextNodeForFontFaceCSSRule(fontConfig, fontsAsBase64[fontConfig.fontFamily]);
        styleElem.appendChild(fontFaceTextNode);
    });
    $(svg).prepend(styleElem);
}

// We serialize the SVG in preparation for using the serialized
// version in an image data URL. We also replace some XHTML that
// prevents the image from rendering properly with some escaped
// values that won't cause the image to break.
export function getSVGDataUrl(svg: Element) {
    const serializedSVG = new window.XMLSerializer().serializeToString(svg);
    const escapedXHTML = serializedSVG.replace(/#/g, '%23').replace(/\n/g, '%0A');
    return `data:image/svg+xml;charset=utf-8,${escapedXHTML}`;
}

export async function getSVGAsPNG(clone: Element, width: number, height: number) {
    const dataURL = getSVGDataUrl(clone);

    // We render the SVG inside of an image so that we can later
    // draw the image to a canvas, which we can then save to the
    // file system.

    // We generate an initial throwaway image because of a bug in Safari
    // where no fonts show up in the final image until the second time
    await getImage(dataURL);
    const image = await getImage(dataURL);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')!.drawImage(image, 0, 0);

    // Convert the canvas data to a Blob so that we can
    // save it to the file system as a PNG.
    // See https://github.com/tsayen/dom-to-image.
    const binaryString = window.atob(canvas.toDataURL().split(',')[1]);
    const binaryStringLength = binaryString.length;
    const binaryArray = new Uint8Array(binaryStringLength);
    for (let i = 0; i < binaryStringLength; i++) {
        binaryArray[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([binaryArray], {
        type: 'image/png',
    });
    return blob;
}

export function getImage(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise(res => {
        const image = new Image();
        image.src = dataUrl;
        image.onload = () => res(image);
    });
}

export function getFontDataUrl(fontConfig: FontConfig, base64EncodedFont: string) {
    return `data:${fontConfig.mimeType};charset=utf-8;base64,${base64EncodedFont}`;
}

export function getFontFaceCSSRule(fontConfig: FontConfig, base64EncodedFont: string) {
    const dataUrl = getFontDataUrl(fontConfig, base64EncodedFont);
    return `@font-face { font-family: '${fontConfig.fontFamily}'; src: url(${dataUrl}) format('${fontConfig.format}'); font-style: normal; font-display: block; }`;
}

export function getTextNodeForFontFaceCSSRule(fontConfig: FontConfig, base64EncodedFont: string) {
    const fontFaceRule = getFontFaceCSSRule(fontConfig, base64EncodedFont);
    return document.createTextNode(fontFaceRule);
}

export function getLinkElemForBase64FontFace(fontConfig: FontConfig, base64EncodedFont: string) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = fontConfig.mimeType;
    link.href = getFontDataUrl(fontConfig, base64EncodedFont);
    return link;
}

export function arrayBufferToBase64(data: ArrayBuffer) {
    // String.fromCharCode on the full array exceeds the max call stack in Safari.
    const binaryArray = new Uint8Array(data);
    const chunkSize = 0x8000;
    let binaryString = '';
    for (let i = 0; i < binaryArray.length; i += chunkSize) {
        binaryString += String.fromCharCode(...binaryArray.subarray(i, i + chunkSize));
    }

    return window.btoa(binaryString);
}

export function blobToDataURL(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = _e => resolve(reader.result as string);
        reader.onerror = _e => reject(reader.error);
        reader.onabort = _e => reject(new Error('Read aborted'));
        reader.readAsDataURL(blob);
    });
}

export async function shareSupported(): Promise<boolean> {
    return Capacitor.isNativePlatform();
}

const SHARED_FILES_DIR = 'shared_files';

// The share sheet and the file opener only accept file:// URIs. FileReader is not safe here: cordova-plugin-file replaces it.
async function writeBlobToCache(blob: Blob, fileName: string): Promise<string> {
    await Filesystem.rmdir({ path: SHARED_FILES_DIR, directory: Directory.Cache, recursive: true }).catch(() => {});
    const { uri } = await Filesystem.writeFile({
        path: `${SHARED_FILES_DIR}/${fileName}`,
        data: arrayBufferToBase64(await blob.arrayBuffer()),
        directory: Directory.Cache,
        recursive: true,
    });
    return uri;
}

function notifyErrorLogService(message: string, error: unknown, level: string, fileName: string) {
    angularInjectorProvider
        .safeGet<typeof ErrorLogServiceClass>('ErrorLogService')
        ?.notify(message, error, { level, file_name: fileName });
}

function shareUri(uri: string, fileName: string) {
    Share.share({ files: [uri] }).catch((error: unknown) => {
        if (error instanceof Error && error.message === 'Share canceled') return;
        notifyErrorLogService('Failed to share a file', error, 'error', fileName);
    });
}

// On Android, the file opener rejects when no installed app opens the file type. The share sheet always has a target.
export async function openBlob(blob: Blob, fileName: string): Promise<void> {
    const uri = await writeBlobToCache(blob, fileName);
    try {
        await FileOpener.openFile({ path: uri, mimeType: blob.type });
    } catch (error) {
        if ((error as { code?: string }).code === ErrorCode.FileAlreadyOpen) return;
        notifyErrorLogService('Failed to open a file, falling back to the share sheet', error, 'warning', fileName);
        shareUri(uri, fileName);
    }
}

export async function shareBlob(blob: Blob, fileName: string): Promise<void> {
    shareUri(await writeBlobToCache(blob, fileName), fileName);
}

export function downloadSupported() {
    return !window.CORDOVA;
}
